import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, InputBase, IconButton, Select, MenuItem,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { axios, baseUrl } from 'Api';
import { Page } from 'Components/page';
import { AppContext, AuthContext } from 'Context';
import { Pathname } from 'Routes';
import {
  GridShimmer, ViewerGrid, ViewerThemeProvider, ViewerAurora,
} from 'Components/viewer-inner';
import PermissionDeniedModal from 'Components/PermissionDeniedModal';
import { useProjectorBg } from 'hooks/useProfileBg';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { SearchPageClassic } from './SearchPageClassic';
import styles from './index.module.css';

const SORTS = {
  relevance: { label: 'Relevance', fn: null },
  az: { label: 'Name A–Z', fn: (a, b) => (a.title || '').localeCompare(b.title || '') },
  za: { label: 'Name Z–A', fn: (a, b) => (b.title || '').localeCompare(a.title || '') },
};

const DARK_SELECT_SX = {
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  bgcolor: 'rgba(255,255,255,0.06)',
  borderRadius: 999,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.14)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiSelect-select': { py: 1, pl: 2 },
};

const SearchPageNew = () => {
    // eslint-disable-next-line no-script-url
    window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const { userDetails } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const { profileID } = useParams();
  const projectorBg = useProjectorBg(profileID);
  const [profileName, setProfileName] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [sortMode, setSortMode] = useState('relevance');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const timerRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (userDetails?.firstname) setProfileName(userDetails?.firstname?.substring(0, 10));
    else if (userDetails?.email) setProfileName(userDetails?.email?.split('@')[0]?.substring(0, 10));
  }, [userDetails]);

  // Debounced search. A request id guards against out-of-order responses so
  // the grid always reflects the latest query.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = searchTerm.trim();
    if (!term) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      reqRef.current += 1;
      const reqId = reqRef.current;
      try {
        const response = await axios.post(`${baseUrl}/searchVideo`, {
          search_key: term,
          token: getToken(),
          user_id: userDetails?.myId,
        });
        if (reqId !== reqRef.current) return; // a newer query superseded this one
        const vids = response?.data?.videos;
        setResults(Array.isArray(vids) ? vids : []);
      } catch (e) {
        if (reqId === reqRef.current) setResults([]);
      } finally {
        if (reqId === reqRef.current) setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [searchTerm]);

  const sortedResults = useMemo(() => {
    const fn = SORTS[sortMode]?.fn;
    return fn ? [...results].sort(fn) : results;
  }, [results, sortMode]);

  const term = searchTerm.trim();

  return (
    <Page noBottomBar noFixedSidebar noDefaultBg>
      <ViewerThemeProvider>
      <ViewerAurora color={projectorBg} />
      <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
      <Box sx={{ width: '86%', mx: 'auto', px: { xs: '12px', md: '20px' }, py: 3, pb: 6 }}>
        {/* Search + sort controls */}
        <Box sx={{
          display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 3,
        }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 240,
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            '&:focus-within': { borderColor: 'rgba(255,255,255,0.4)' },
          }}
          >
            <SearchRoundedIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />
            <InputBase
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search videos"
              sx={{ flex: 1, color: '#fff', fontSize: 16, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
            />
            {searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            size="small"
            sx={DARK_SELECT_SX}
            MenuProps={{ PaperProps: { sx: { bgcolor: '#17171c', color: '#fff', '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(255,255,255,0.08)' } } } }}
          >
            {Object.entries(SORTS).map(([key, s]) => (
              <MenuItem key={key} value={key}>{s.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Results / states */}
        {searching ? (
          <Box sx={{ mx: { xs: '-10px', md: '-15px' } }}><GridShimmer /></Box>
        ) : !term ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <SearchRoundedIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)', mb: 1.5 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 16 }}>Search for videos across this projector.</Typography>
          </Box>
        ) : sortedResults.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff', mb: 1 }}>{`No results for “${term}”`}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Try a different title or keyword.</Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mb: 2 }}>
              {`${sortedResults.length} ${sortedResults.length === 1 ? 'result' : 'results'} for `}
              <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>{`“${term}”`}</Box>
            </Typography>
            <Box sx={{ mx: { xs: '-10px', md: '-15px' } }}>
              <ViewerGrid videos={sortedResults} onLockedClick={() => setShowPermissionModal(true)} />
            </Box>
          </>
        )}
      </Box>
      </Box>
      <PermissionDeniedModal
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
      />
      </ViewerThemeProvider>
    </Page>
  );
};

export const SearchPage = (props) => (
  isNewUIEnabled() ? <SearchPageNew {...props} /> : <SearchPageClassic {...props} />
);
